/**
 * LiveKitConnection - WebRTC connection management for voice agent communication
 *
 * This class manages the core WebRTC connection to LiveKit rooms, handling connection
 * lifecycle, participant management, and real-time communication state. It serves as
 * the foundation for voice agent interactions by providing reliable, scalable WebRTC
 * connectivity with automatic reconnection and comprehensive event handling.
 *
 * Key Features:
 * - **Robust Connection Management**: Automatic connection establishment, monitoring, and cleanup
 * - **Intelligent Reconnection**: Built-in reconnection logic with attempt tracking
 * - **Participant Tracking**: Real-time monitoring of room participants and their metadata
 * - **Connection Quality Optimization**: Adaptive streaming and bandwidth management
 * - **Pause/Resume Functionality**: Microphone control for conversation flow management
 * - **Comprehensive Event System**: Detailed events for connection state changes
 * - **Performance Monitoring**: Connection timing and attempt statistics
 *
 * Connection Lifecycle:
 * 1. **Initialization**: Room setup with optimized WebRTC configuration
 * 2. **Connection**: Secure connection establishment with performance tracking
 * 3. **Monitoring**: Real-time connection quality and participant management
 * 4. **Reconnection**: Automatic recovery from network issues
 * 5. **Cleanup**: Proper resource disposal and state management
 *
 * WebRTC Configuration:
 * - Adaptive streaming for optimal bandwidth usage
 * - Dynacast for efficient bandwidth management
 * - HD video capability (720p) for future video features
 * - Automatic echo cancellation and noise suppression
 *
 * @example Basic Connection Management
 * ```typescript
 * const connection = new LiveKitConnection(
 *   'wss://livekit.example.com',
 *   'jwt_access_token_here'
 * );
 *
 * // Set up event listeners
 * connection.on('connected', () => {
 *   console.log('Connected to voice agent room');
 *   enableVoiceInterface();
 * });
 *
 * connection.on('disconnected', () => {
 *   console.log('Disconnected from room');
 *   disableVoiceInterface();
 * });
 *
 * connection.on('reconnecting', () => {
 *   showReconnectingIndicator();
 * });
 *
 * // Connect to room
 * await connection.connect();
 * ```
 *
 * @example Participant Management
 * ```typescript
 * connection.on('participantConnected', (participant) => {
 *   console.log(`${participant.identity} joined the conversation`);
 *
 *   if (participant.identity.includes('agent')) {
 *     showAgentStatus('online');
 *     enableAgentFeatures();
 *   }
 * });
 *
 * connection.on('participantDisconnected', (participant) => {
 *   console.log(`${participant.identity} left the conversation`);
 *
 *   if (participant.identity.includes('agent')) {
 *     showAgentStatus('offline');
 *     handleAgentDisconnection();
 *   }
 * });
 *
 * // Get current participants
 * const participants = connection.getParticipants();
 * const agentPresent = participants.some(p => p.identity.includes('agent'));
 * ```
 *
 * @example Connection Quality Monitoring
 * ```typescript
 * connection.on('connectionStateChanged', (state) => {
 *   switch (state) {
 *     case 'connected':
 *       hideConnectionWarnings();
 *       break;
 *     case 'reconnecting':
 *       showConnectionIssueWarning();
 *       break;
 *     case 'disconnected':
 *       showConnectionLostError();
 *       break;
 *   }
 * });
 *
 * // Monitor connection statistics
 * const stats = connection.getConnectionStats();
 * console.log(`Connection attempts: ${stats.connectionAttempts}`);
 * console.log(`Reconnection attempts: ${stats.reconnectionAttempts}`);
 * console.log(`Participants: ${stats.participantCount}`);
 * ```
 *
 * @example Pause/Resume Functionality
 * ```typescript
 * // Pause conversation (mute microphone)
 * pauseButton.addEventListener('click', () => {
 *   connection.pause();
 *   showPausedIndicator();
 * });
 *
 * // Resume conversation (unmute microphone)
 * resumeButton.addEventListener('click', () => {
 *   connection.resume();
 *   hidePausedIndicator();
 * });
 *
 * // Listen for pause/resume events
 * connection.on('connectionPaused', () => {
 *   updateUIForPausedState();
 * });
 *
 * connection.on('connectionResumed', () => {
 *   updateUIForActiveState();
 * });
 * ```
 *
 * @example Error Handling
 * ```typescript
 * connection.on('connectionError', (error) => {
 *   console.error('Connection error:', error.message);
 *
 *   // Implement retry logic
 *   if (error.message.includes('authentication')) {
 *     handleAuthenticationError();
 *   } else if (error.message.includes('network')) {
 *     handleNetworkError();
 *     // Automatic reconnection will be attempted
 *   }
 * });
 *
 * // Monitor reconnection attempts
 * connection.on('reconnecting', () => {
 *   const attempts = connection.getReconnectionAttempts();
 *   if (attempts > 3) {
 *     suggestNetworkTroubleshooting();
 *   }
 * });
 * ```
 *
 * Technical Implementation:
 * - Built on LiveKit's v2.15.4 WebRTC infrastructure
 * - Supports adaptive streaming for bandwidth optimization
 * - Implements connection pre-warming for faster establishment
 * - Provides comprehensive event forwarding from LiveKit room events
 * - Maintains participant metadata and connection timing information
 * - Includes fallback mechanisms for connection reliability
 */

import { EventEmitter } from 'events';
import {
  type ConnectionState,
  type DataChannelKind,
  type Participant,
  type RemoteParticipant,
  Room,
  RoomEvent,
  VideoPresets,
} from 'livekit-client';
import { createDebugLogger, type DebugLogger } from '../utils';
import type { ParticipantData } from './types';

const IOS_REGEX = /iP(hone|ad|od)|iPhone/i;

/**
 * LiveKitConnection class for managing WebRTC connections to voice agent rooms
 *
 * Extends EventEmitter to provide real-time connection status updates and
 * participant management events for voice agent applications.
 */
export class LiveKitConnection extends EventEmitter {
  /** LiveKit room instance for WebRTC communication */
  room: Room | null = null;

  /** Current connection status to the LiveKit room */
  isConnected = false;

  /** Whether the conversation is currently paused (microphone muted) */
  isPaused = false;

  /** Map of active participants in the room with their metadata */
  participants: Map<string, ParticipantData> = new Map();

  /** Unix timestamp when the connection/call was initiated */
  callStartTime: number | null = null;

  /** LiveKit WebSocket URL for room connection */
  private readonly lkUrl: string;

  /** JWT access token for room authentication */
  private readonly accessToken: string;

  /** Counter for total connection attempts (including retries) */
  // biome-ignore lint/style/useReadonlyClassProperties: connectionAttempts is incremented in connect()
  private connectionAttempts = 0;

  /** Counter for reconnection attempts after initial connection */
  private reconnectionAttempts = 0;

  /** Whether we've already emitted a 'connected' event for the current session */
  private hasEmittedConnected = false;

  /** Debug logger instance for conditional logging */
  private readonly logger: DebugLogger;

  /** When true, the session is text/chat-only and the microphone is never enabled */
  private readonly isChatOnly: boolean;

  /**
   * Creates a new LiveKitConnection instance
   *
   * Initializes a LiveKit room with optimized WebRTC configuration for
   * voice agent communication, including adaptive streaming and bandwidth
   * management features.
   *
   * @param lkUrl - LiveKit WebSocket URL (e.g., 'wss://livekit.example.com')
   * @param accessToken - JWT token for room authentication and authorization
   * @param debug - Enable debug logging (defaults to false)
   * @param isChatOnly - When true, the microphone is never enabled (text/chat-only session)
   *
   * @example
   * ```typescript
   * const connection = new LiveKitConnection(
   *   'wss://rtc.eu.tryhamsa.com',
   *   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
   *   false
   * );
   *
   * // Connection is ready for use
   * await connection.connect();
   * ```
   */
  constructor(
    lkUrl: string,
    accessToken: string,
    debug = false,
    isChatOnly = false
  ) {
    super();
    this.lkUrl = lkUrl;
    this.accessToken = accessToken;
    this.logger = createDebugLogger(debug);
    this.isChatOnly = isChatOnly;

    // Initialize LiveKit Room with optimal configuration for voice agents
    this.room = new Room({
      // Enable adaptive streaming for optimal bandwidth usage
      adaptiveStream: true,
      // Enable dynacast for efficient bandwidth management
      dynacast: true,
      // Configure video settings for potential future video features
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution,
      },
      // Enable echo cancellation and noise suppression for clearer audio
      audioCaptureDefaults: {
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    // Set up event handlers for room lifecycle management
    this.#setupRoomEventHandlers();
  }

  /**
   * Configures comprehensive event handlers for LiveKit room lifecycle management
   *
   * Sets up listeners for all critical room events including connection state changes,
   * participant management, and reconnection handling. These handlers forward events
   * to the LiveKitConnection EventEmitter for external consumption.
   *
   * @private
   */
  #setupRoomEventHandlers(): void {
    if (!this.room) {
      return;
    }

    this.room
      .on(RoomEvent.Connected, this.#handleConnected.bind(this))
      .on(RoomEvent.Disconnected, this.#handleDisconnected.bind(this))
      .on(RoomEvent.Reconnecting, this.#handleReconnecting.bind(this))
      .on(RoomEvent.Reconnected, this.#handleReconnected.bind(this))
      .on(
        RoomEvent.ParticipantConnected,
        this.#handleParticipantConnected.bind(this)
      )
      .on(
        RoomEvent.ParticipantDisconnected,
        this.#handleParticipantDisconnected.bind(this)
      )
      .on(
        RoomEvent.ParticipantAttributesChanged,
        this.#handleParticipantAttributesChanged.bind(this)
      )
      .on(
        RoomEvent.ConnectionStateChanged,
        this.#handleConnectionStateChanged.bind(this)
      )
      .on(RoomEvent.SignalConnected, this.#handleSignalConnected.bind(this))
      .on(
        RoomEvent.SignalReconnecting,
        this.#handleSignalReconnecting.bind(this)
      )
      .on(RoomEvent.MediaDevicesError, this.#handleMediaDevicesError.bind(this))
      .on(
        RoomEvent.ConnectionQualityChanged,
        this.#handleConnectionQualityChanged.bind(this)
      )
      .on(
        RoomEvent.DCBufferStatusChanged,
        this.#handleDCBufferStatusChanged.bind(this)
      );
  }

  /**
   * Provides access to the underlying LiveKit room instance
   *
   * Exposes the LiveKit room for advanced operations that require direct
   * access to the WebRTC room functionality. Use with caution as direct
   * manipulation may interfere with connection management.
   *
   * @returns LiveKit Room instance or null if not initialized
   *
   * @example
   * ```typescript
   * const room = connection.getRoom();
   * if (room) {
   *   // Access advanced LiveKit features
   *   console.log('Room name:', room.name);
   *   console.log('Local participant:', room.localParticipant?.identity);
   * }
   * ```
   */
  getRoom(): Room | null {
    return this.room;
  }

  /**
   * Establishes connection to the LiveKit room with performance optimization
   *
   * Initiates the WebRTC connection process with connection pre-warming for
   * faster establishment times. Tracks connection performance metrics and
   * handles authentication through the provided JWT token.
   *
   * @throws {Error} Connection failures due to network issues, authentication problems, or server unavailability
   *
   * @example
   * ```typescript
   * try {
   *   // Set up event listeners first
   *   connection.on('connected', () => {
   *     console.log('Successfully connected to voice agent');
   *     enableVoiceFeatures();
   *   });
   *
   *   connection.on('connectionError', (error) => {
   *     console.error('Connection failed:', error.message);
   *     handleConnectionFailure(error);
   *   });
   *
   *   // Initiate connection
   *   await connection.connect();
   * } catch (error) {
   *   console.error('Failed to connect:', error);
   * }
   * ```
   *
   * @example Connection Performance Monitoring
   * ```typescript
   * connection.on('connectionEstablished', (timeMs) => {
   *   console.log(`Connection established in ${timeMs}ms`);
   *
   *   if (timeMs > 5000) {
   *     logSlowConnection(timeMs);
   *   }
   * });
   *
   * await connection.connect();
   * ```
   */
  async connect(): Promise<void> {
    try {
      this.logger.log('connect() called', {
        source: 'LiveKitConnection',
        error: {
          lkUrl: this.lkUrl,
          attempt: this.connectionAttempts + 1,
          stack: new Error('Connection call stack').stack,
        },
      });

      // Record call initiation time for performance metrics
      this.callStartTime = Date.now();
      this.connectionAttempts++;

      // Pre-warm connection on non-iOS platforms only.
      // iOS Safari can suspend the tab between prepareConnection and connect,
      // leaving a stale WebSocket that causes the initial handshake to fail.
      const isIOS =
        typeof navigator !== 'undefined' && IOS_REGEX.test(navigator.userAgent);
      if (!isIOS) {
        this.room?.prepareConnection(this.lkUrl, this.accessToken);
      }

      // Track connection establishment performance
      const connectionStart = Date.now();

      // Establish secure WebRTC connection to LiveKit room.
      // maxRetries: default is 1, which fails on iOS when Safari suspends the
      // tab mid-handshake. Bump to 3 so transient drops on the initial signal
      // WebSocket get retried before surfacing an error.
      await this.room?.connect(this.lkUrl, this.accessToken, {
        maxRetries: 3,
      });

      this.logger.log('room.connect() completed successfully', {
        source: 'LiveKitConnection',
        error: {
          connectionTimeMs: Date.now() - connectionStart,
          roomName: this.room?.name,
          state: this.room?.state,
        },
      });

      // Emit connection timing metrics for performance monitoring
      const connectionTime = Date.now() - connectionStart;
      this.emit('connectionEstablished', connectionTime);

      // Mark as connected and emit 'connected' once. In some test environments,
      // RoomEvent.Connected may not fire, so we emit here as a fallback.
      this.isConnected = true;
      if (!this.hasEmittedConnected) {
        this.emit('connected');
        this.hasEmittedConnected = true;
      }
    } catch (error) {
      this.logger.error('connect() failed', {
        source: 'LiveKitConnection',
        error: {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
      });

      this.emit(
        'connectionError',
        new Error(
          `LiveKit connection failed: ${error instanceof Error ? error.message : String(error)}`
        )
      );
    }
  }

  /**
   * Force disconnection for testing
   */
  forceDisconnect(): void {
    this.isConnected = false;
    this.emit('disconnected');
    this.#cleanup();
  }

  /**
   * Disconnects from the LiveKit room
   */
  async disconnect(): Promise<void> {
    const disconnectStartTime = Date.now();
    try {
      this.logger.log('LiveKitConnection.disconnect() - START', {
        source: 'LiveKitConnection',
        error: {
          timestamp: disconnectStartTime,
          isConnected: this.isConnected,
          roomName: this.room?.name,
          state: this.room?.state,
          participantCount: this.participants.size,
          hasRoom: !!this.room,
          stack: new Error('Disconnect call stack').stack,
        },
      });

      if (this.room) {
        const roomDisconnectStart = Date.now();
        this.logger.log('Calling room.disconnect() (LiveKit SDK)', {
          source: 'LiveKitConnection',
          error: {
            timestamp: roomDisconnectStart,
            roomName: this.room?.name,
            state: this.room?.state,
          },
        });

        await this.room.disconnect();

        const roomDisconnectEnd = Date.now();
        this.logger.log('room.disconnect() completed (LiveKit SDK)', {
          source: 'LiveKitConnection',
          error: {
            timestamp: roomDisconnectEnd,
            roomDisconnectDuration: roomDisconnectEnd - roomDisconnectStart,
            roomName: this.room?.name,
            state: this.room?.state,
          },
        });
      } else {
        this.logger.log('No room to disconnect (room is null)', {
          source: 'LiveKitConnection',
          error: {
            timestamp: Date.now(),
          },
        });
      }

      const cleanupStart = Date.now();
      this.#cleanup();
      const cleanupEnd = Date.now();

      this.logger.log('LiveKitConnection.disconnect() - COMPLETE', {
        source: 'LiveKitConnection',
        error: {
          timestamp: cleanupEnd,
          cleanupDuration: cleanupEnd - cleanupStart,
          totalDisconnectDuration: cleanupEnd - disconnectStartTime,
        },
      });
    } catch (error) {
      const errorTime = Date.now();
      this.logger.error('LiveKitConnection.disconnect() - FAILED', {
        source: 'LiveKitConnection',
        error: {
          timestamp: errorTime,
          timeSinceStart: errorTime - disconnectStartTime,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
      });

      this.emit(
        'connectionError',
        new Error(
          `LiveKit disconnection failed: ${error instanceof Error ? error.message : String(error)}`
        )
      );
    }
  }

  /**
   * Pauses the connection by muting local microphone
   */
  pause(): void {
    try {
      this.isPaused = true;

      // Mute local microphone
      if (this.room?.localParticipant) {
        this.room.localParticipant.setMicrophoneEnabled(false);
      }

      this.emit('connectionPaused');
    } catch (error) {
      this.emit(
        'connectionError',
        new Error(
          `Failed to pause call: ${error instanceof Error ? error.message : String(error)}`
        )
      );
    }
  }

  /**
   * Resumes the connection by unmuting local microphone
   */
  resume(): void {
    try {
      this.isPaused = false;

      // Unmute local microphone
      if (this.room?.localParticipant) {
        this.room.localParticipant.setMicrophoneEnabled(true);
      }

      this.emit('connectionResumed');
    } catch (error) {
      this.emit(
        'connectionError',
        new Error(
          `Failed to resume call: ${error instanceof Error ? error.message : String(error)}`
        )
      );
    }
  }

  /**
   * Gets connection statistics
   */
  getConnectionStats(): {
    connectionAttempts: number;
    reconnectionAttempts: number;
    isConnected: boolean;
    participantCount: number;
  } {
    return {
      connectionAttempts: this.connectionAttempts,
      reconnectionAttempts: this.reconnectionAttempts,
      isConnected: this.isConnected,
      participantCount: this.participants.size,
    };
  }

  /**
   * Gets connection attempts
   */
  getConnectionAttempts(): number {
    return this.connectionAttempts;
  }

  /**
   * Gets reconnection attempts
   */
  getReconnectionAttempts(): number {
    return this.reconnectionAttempts;
  }

  /**
   * Gets current participants
   */
  getParticipants(): ParticipantData[] {
    return Array.from(this.participants.values());
  }

  /**
   * Handles successful room connection
   */
  async #handleConnected(): Promise<void> {
    try {
      this.isConnected = true;

      this.logger.log('Room Connected event fired', {
        source: 'LiveKitConnection',
        error: {
          roomName: this.room?.name,
          localParticipant: this.room?.localParticipant?.identity,
          state: this.room?.state,
        },
      });

      // Enable microphone after connection — skipped for chat-only sessions,
      // which have no audio and must not trigger the browser mic prompt.
      if (this.isChatOnly) {
        this.logger.log('Chat-only session: skipping microphone enable', {
          source: 'LiveKitConnection',
        });
      } else {
        await this.room?.localParticipant?.setMicrophoneEnabled(true);
      }

      // Emit 'connected' only once per session
      if (!this.hasEmittedConnected) {
        this.emit('connected');
        this.hasEmittedConnected = true;
      }
    } catch (error) {
      this.logger.error('Error in handleConnected', {
        source: 'LiveKitConnection',
        error,
      });
      this.emit(
        'connectionError',
        new Error(
          `Failed to initialize after connection: ${error instanceof Error ? error.message : String(error)}`
        )
      );
    }
  }

  /**
   * Handles room disconnection
   */
  #handleDisconnected(): void {
    const disconnectEventTime = Date.now();
    this.logger.log(
      'Room Disconnected event fired (from LiveKit SDK) - TIMING',
      {
        source: 'LiveKitConnection',
        error: {
          timestamp: disconnectEventTime,
          roomName: this.room?.name,
          state: this.room?.state,
          participantCount: this.participants.size,
        },
      }
    );

    this.isConnected = false;

    const emitStart = Date.now();
    this.emit('disconnected');
    const emitEnd = Date.now();

    this.logger.log('disconnected event emitted to parent - TIMING', {
      source: 'LiveKitConnection',
      error: {
        timestamp: emitEnd,
        emitDuration: emitEnd - emitStart,
      },
    });

    const cleanupStart = Date.now();
    this.#cleanup();
    const cleanupEnd = Date.now();

    this.logger.log('Room Disconnected handler complete - TIMING', {
      source: 'LiveKitConnection',
      error: {
        timestamp: cleanupEnd,
        cleanupDuration: cleanupEnd - cleanupStart,
        totalHandlerDuration: cleanupEnd - disconnectEventTime,
      },
    });
  }

  /**
   * Handles reconnection attempts
   */
  #handleReconnecting(): void {
    this.reconnectionAttempts++;

    this.logger.log('Room Reconnecting', {
      source: 'LiveKitConnection',
      error: {
        attempt: this.reconnectionAttempts,
        roomName: this.room?.name,
        state: this.room?.state,
      },
    });

    this.emit('reconnecting');
  }

  /**
   * Handles successful reconnection
   */
  #handleReconnected(): void {
    this.isConnected = true;

    this.logger.log('Room Reconnected successfully', {
      source: 'LiveKitConnection',
      error: {
        roomName: this.room?.name,
        state: this.room?.state,
        participantCount: this.participants.size,
      },
    });

    this.emit('reconnected');
  }

  /**
   * Handles participant connection
   */
  #handleParticipantConnected(participant: RemoteParticipant): void {
    this.logger.log('Participant connected in LiveKit layer', {
      source: 'LiveKitConnection',
      error: {
        identity: participant.identity,
        sid: participant.sid,
        metadata: participant.metadata,
        totalParticipants: this.participants.size + 1,
      },
    });

    this.participants.set(participant.sid || 'unknown', {
      identity: participant.identity || 'unknown',
      sid: participant.sid || 'unknown',
      connectionTime: Date.now(),
      metadata: participant.metadata,
    });
    this.emit('participantConnected', participant);
  }

  /**
   * Handles participant disconnection
   */
  #handleParticipantDisconnected(participant: RemoteParticipant): void {
    this.logger.log('Participant disconnected in LiveKit layer', {
      source: 'LiveKitConnection',
      error: {
        identity: participant.identity,
        sid: participant.sid,
        totalParticipantsBeforeRemoval: this.participants.size,
      },
    });

    this.participants.delete(participant.sid || 'unknown');
    this.emit('participantDisconnected', participant);
  }

  /**
   * Handles participant attributes changed
   * Monitors the 'lk.agent.state' attribute to track agent state changes
   */
  #handleParticipantAttributesChanged(
    changedAttributes: Record<string, string>,
    _participant: Participant
  ): void {
    // Check if the agent state attribute changed
    if ('lk.agent.state' in changedAttributes) {
      const agentState = changedAttributes['lk.agent.state'];
      if (agentState !== undefined) {
        this.emit('agentStateChanged', agentState);
      }
    }
  }

  /**
   * Handles connection state changes
   */
  #handleConnectionStateChanged(state: ConnectionState): void {
    this.logger.log('Connection state changed', {
      source: 'LiveKitConnection',
      error: {
        newState: state,
        roomName: this.room?.name,
        participantCount: this.participants.size,
      },
    });

    this.emit('connectionStateChanged', state);
  }

  /**
   * Handles signal connection established
   */
  #handleSignalConnected(): void {
    this.logger.log('Signal connection established', {
      source: 'LiveKitConnection',
      error: {
        roomName: this.room?.name,
        state: this.room?.state,
      },
    });
  }

  /**
   * Handles signal reconnection attempts
   */
  #handleSignalReconnecting(): void {
    this.logger.log('Signal reconnecting (signaling server connection lost)', {
      source: 'LiveKitConnection',
      error: {
        roomName: this.room?.name,
        state: this.room?.state,
        participantCount: this.participants.size,
      },
    });
  }

  /**
   * Handles media devices errors
   */
  #handleMediaDevicesError(error: Error): void {
    this.logger.error('Media devices error', {
      source: 'LiveKitConnection',
      error: {
        error: error.message,
        stack: error.stack,
        roomName: this.room?.name,
      },
    });
  }

  /**
   * Handles connection quality changes
   */
  #handleConnectionQualityChanged(
    quality: string,
    participant: Participant
  ): void {
    this.logger.log('Connection quality changed', {
      source: 'LiveKitConnection',
      error: {
        participant: participant.identity,
        quality,
        roomName: this.room?.name,
      },
    });
  }

  /**
   * Handles DataChannel buffer status changes
   * This is critical for debugging DataChannel errors
   */
  #handleDCBufferStatusChanged(isLow: boolean, kind: DataChannelKind): void {
    this.logger.log('DataChannel buffer status changed', {
      source: 'LiveKitConnection',
      error: {
        isLow,
        kind,
        roomName: this.room?.name,
        participantCount: this.participants.size,
      },
    });
  }

  /**
   * Cleans up connection resources
   */
  #cleanup(): void {
    // Clear participant data
    this.participants.clear();
    this.isConnected = false;
    this.isPaused = false;
    this.hasEmittedConnected = false;

    // Note: Event listeners on the room are automatically cleaned up
    // when room.disconnect() is called by the LiveKit SDK
  }

  /**
   * Public cleanup method for external access
   */
  cleanup(): void {
    this.#cleanup();
  }
}
